import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Bell, Menu, Shield } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';

/**
 * ResponsiveAdminHeader - Premium Design
 * Exactly matches the Owner's ResponsiveTopHeader.js styling
 */
const ResponsiveAdminHeader = ({ onMenuClick, user, isMobile = false }) => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const getInitials = (name) => {
    return name
      ?.split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <header className={`
      h-14 flex items-center justify-between px-4 sm:px-6 flex-shrink-0
      bg-gradient-to-b from-white to-slate-50/80
      backdrop-blur-md
      relative
      ${isMobile ? 'fixed top-0 left-0 right-0 z-30 shadow-sm' : ''}
    `}>
      {/* Inner glass highlight */}
      <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-transparent to-transparent pointer-events-none" />
      {/* Subtle top glow line */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white to-transparent" />
      
      {/* Left: Hamburger + Title */}
      <div className="flex items-center gap-3 relative z-10">
        {/* Hamburger Menu - Mobile Only */}
        <button
          onClick={onMenuClick}
          className="p-2 hover:bg-white/80 rounded-lg transition-all lg:hidden"
          data-testid="admin-mobile-menu-btn"
        >
          <Menu className="w-5 h-5 text-slate-600" strokeWidth={1.5} />
        </button>
        
        <div className="flex items-center gap-2">
          <Shield className="w-4 h-4 text-rose-500" />
          <h2 className="text-[14px] sm:text-[15px] font-medium text-slate-700 truncate tracking-tight">
            Admin Console
          </h2>
        </div>
      </div>

      {/* Right: Admin Badge, Notifications, User Menu */}
      <div className="flex items-center gap-2 sm:gap-3 relative z-10">
        {/* Admin Badge - Premium styled */}
        <div className="hidden md:flex relative px-2.5 py-1 bg-gradient-to-r from-rose-100/80 to-red-100/80 border border-rose-200/50 rounded-md shadow-sm overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-white/40 to-transparent pointer-events-none" />
          <span className="relative text-[10px] font-medium text-rose-700 tracking-wide">Super Admin</span>
        </div>

        {/* Notifications */}
        <button className="relative p-2 hover:bg-white/80 rounded-lg transition-all">
          <Bell className="w-4 h-4 text-slate-500" strokeWidth={1.5} />
          <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 bg-rose-500 rounded-full"></span>
        </button>

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-2" data-testid="admin-user-menu-trigger">
            <Avatar className="w-7 h-7 sm:w-8 sm:h-8 ring-2 ring-white/80 shadow-sm">
              <AvatarFallback className="bg-rose-600 text-white text-[10px] sm:text-[11px] font-medium">
                {getInitials(user?.name || 'A')}
              </AvatarFallback>
            </Avatar>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div>
                <div className="font-medium text-slate-700 text-[13px]">{user?.name || 'Admin'}</div>
                <div className="text-[11px] text-slate-400">
                  {user?.email || 'admin@billsera.com'}
                </div>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              className="text-[13px] cursor-pointer"
              onClick={() => navigate('/admin/settings')}
            >
              Admin Settings
            </DropdownMenuItem>
            <DropdownMenuItem 
              className="text-[13px] cursor-pointer"
              onClick={() => navigate('/admin/audit-logs')}
            >
              Audit Logs
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => {
                logout();
                navigate('/login');
              }} 
              className="text-rose-600 cursor-pointer text-[13px]"
            >
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default ResponsiveAdminHeader;
